import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "~/components/content/content-item-render/hoc";
import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeMultimediaContentItemContent$key } from "~/queries/__generated__/homeMultimediaContentItemContent.graphql";

import { getLabel } from "./helpers";
import {
  Container,
  Content,
  CoverImage,
  GalleryContainer,
  GalleryTotal,
  Headline,
  Label,
  StyledEntityLink,
  StyledGalleryIcon,
} from "./styles";

export type Props = {
  reference: homeMultimediaContentItemContent$key;
} & ContentItemRenderVariantProps;

const Component = forwardRef<HTMLDivElement, Props>(
  ({ className, onClick, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeMultimediaContentItemContent on Content {
          entityId
          ... on Gallery {
            images {
              __typename
            }
          }
          ...entityLink
          ...helpersGetLabelContent
          ...hooksContentItemProvidersContent
            @arguments(
              withHeadline: true
              withCoverImage: true
              withCoverImageSize540x360: true
              withCoverImageSize1200x800: true
              withVideoDurationOverlay: true
            )
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);

    const label = getLabel(content);

    const galleryTotalImages = content?.images?.length;

    const isVideo = label === "Video";

    return (
      <Container className={className} ref={reference}>
        <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
          <Content>
            <Label>{label}</Label>
            <Headline>
              {providers.headline({
                hideTooltip: true,
                preferSocialHeadline: true,
                skipHeadlineSponsorStyle: true,
              })}
            </Headline>
          </Content>
          <CoverImage $isVideo={isVideo}>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size540x360",
                mobileUp: "size1200x800",
                tabletUp: "size540x360",
              },
              withCaption: false,
            })}
            {providers.video?.durationOverlay({
              variant: "homepage",
            })}
            {galleryTotalImages && (
              <GalleryContainer>
                <StyledGalleryIcon />
                <GalleryTotal>{galleryTotalImages}</GalleryTotal>
              </GalleryContainer>
            )}
          </CoverImage>
        </StyledEntityLink>
      </Container>
    );
  },
);

Component.displayName = "ContentItemHomeMultimedia";

export const ContentItemHomeMultimedia = withHighlightedHeadline(Component);
